// ManagementPage.tsx
import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { apiClient } from '../lib/api'
import {
  ChevronLeft,
  LayoutDashboard,
  FileText,
  DollarSign,
  Tag,
  Globe,
  Plus,
  Edit,
  Trash2,
  Search,
  Download,
  RefreshCw,
  Maximize,
  Eye,
  TrendingUp,
  Users,
  Settings,
  Printer,
  CheckCircle,
  AlertCircle,
  Store,
  BarChart3,
  ShoppingCart,
  Package,
  UserCheck,
  Store as StoreIcon,
  MapPin,
  FileCheck,
  Hash
} from 'lucide-react'
import toast from 'react-hot-toast'

// Custom Components
import { ErrorBoundary } from '../components/ui/ErrorBoundary'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { DataTable, Column } from '../components/ui/DataTable'

import { exportEngine } from '../lib/exportEngine'
import { importEngine } from '../lib/importEngine'
import { auditTrail } from '../lib/auditTrail'

// =====================================================
// TYPE DEFINITIONS (Schema Aligned)
// =====================================================

// Dashboard Analytics
interface DashboardAnalytics {
  totalBusinessDays?: number
  openBusinessDays?: number
  totalSales?: number
  totalTransactions?: number
  totalTax?: number
  activeUsers?: number
  activeStores?: number
  paymentTypeCount?: number
  taxRateCount?: number
  cashMovements?: number
  averageOrderValue?: number
  totalProducts?: number
  totalCustomers?: number
  pendingOrders?: number
  dailyRevenue?: number
}

// Enhanced Documents Tab Types
interface FiscalDocumentUI {
  id: string
  document_number: string
  document_type: string
  document_date: string
  order_id?: string
  cash_register_id?: string
  total_amount: number
  tax_amount: number
  status: string
  is_voided: boolean
  created_at: string
  fiscal_sequence_id?: string
}

interface FiscalSequenceUI {
  id: string
  cash_register_id?: string
  sequence_type: string
  prefix?: string
  current_number: number
  min_number?: number
  max_number?: number
  year?: number
  is_active: boolean
  created_at: string
}

// Enhanced Price Lists Types
interface PriceLevelUI {
  id: string
  code: string
  name: string
  description?: string
  markup_percentage?: number
  is_default: boolean
  is_active: boolean
  sort_order: number
  created_at: string
}

interface ProductPriceUI {
  id: string
  product_id: string
  price_level: string
  price: number
  effective_date: string
  product?: {
    id: string
    code: string
    name: string
  }
}

// Enhanced Promotions Types
interface DiscountTypeUI {
  id: string
  code: string
  name: string
  discount_type: string
  value: number
  is_active: boolean
  requires_authorization: boolean
  min_amount?: number
  max_amount?: number
  start_date?: string
  end_date?: string
  created_at: string
}

interface MarketingCampaignUI {
  id: string
  campaign_name: string
  campaign_code?: string
  description?: string
  campaign_type?: string
  start_date?: string
  end_date?: string
  budget?: number
  discount_percentage?: number
  is_active: boolean
  created_at: string
}

// Enhanced Security Types
interface UserUI {
  id: string
  username: string
  first_name?: string
  last_name?: string
  email?: string
  employee_number?: string
  role: string
  is_active: boolean
  can_authorize_discounts: boolean
  can_authorize_voids: boolean
  can_authorize_refunds: boolean
  created_at: string
}

// UserRoleUI aligned with user_roles table in schema
interface UserPermissionUI {
  id: string
  user_id: string
  role_id: string
  assigned_at: string
  assigned_by?: string
  expires_at?: string
  is_active: boolean
  user?: {
    username: string
    first_name?: string
    last_name?: string
    is_admin: boolean
  }
  role?: {
    code: string
    name: string
    is_active: boolean
  }
}

// Enhanced Payment Types — aligned with payment_types table in schema
interface PaymentTypeUI {
  id: string
  code: string
  name: string
  is_active: boolean
  opens_cash_drawer: boolean
  requires_reference: boolean
  allows_change: boolean
  is_default: boolean
  sort_order: number
  image_url?: string
  color?: string
  created_at: string
}

// Enhanced Countries/Tax Jurisdictions
interface TaxJurisdictionUI {
  id: string
  code: string
  name: string
  country?: string
  state?: string
  tax_authority?: string
  fiscal_year_start?: string
  is_active: boolean
  created_at: string
}

interface RegionUI {
  id: string
  code?: string
  name: string
  country?: string
  regional_manager_id?: string
  is_active: boolean
  created_at: string
}

// Enhanced Company Types — aligned with companies table in schema
interface CompanyUI {
  id: string
  name: string
  legal_name?: string
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  postal_code?: string
  country_id?: string
  phone?: string
  email?: string
  tax_number?: string
  vat_number?: string
  registration_number?: string
  logo_url?: string
  website?: string
  currency_id?: string
  timezone?: string
  fiscal_year_start?: number
  is_active: boolean
  created_at: string
}

// StoreUI aligned with branches table in schema
interface StoreUI {
  id: string
  company_id?: string
  region_id?: string
  territory_id?: string
  code: string
  name: string
  branch_type?: string
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  postal_code?: string
  phone?: string
  email?: string
  manager_id?: string
  is_pos_enabled: boolean
  is_ecommerce_enabled: boolean
  is_active: boolean
  created_at: string
}

// CashRegisterUI aligned with cash_registers table in schema
interface CashRegisterUI {
  id: string
  branch_id: string
  company_id?: string
  register_number: string
  name: string
  register_type?: string
  is_active: boolean
  is_online?: boolean
  ip_address?: string
  mac_address?: string
  hardware_info?: Record<string, unknown>
  last_heartbeat?: string
  current_balance?: string
  created_at: string
}

type TabType = 
  | 'dashboard' 
  | 'documents' 
  | 'price-lists' 
  | 'promotions' 
  | 'countries'

const ITEMS_PER_PAGE = 50
const DEBOUNCE_DELAY = 300

export default function ManagementPage() {
  const { user } = useAuthStore()
  const location = useLocation()
  const initialTab = (new URLSearchParams(location.search).get('tab') as TabType) || 'dashboard'
  const [activeTab, setActiveTab] = useState<TabType>(initialTab)
  const [activeSubTab, setActiveSubTab] = useState<any>('overview')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmDialogProps, setConfirmDialogProps] = useState<any>({})

  // Dashboard Data
  const [analytics, setAnalytics] = useState<DashboardAnalytics>({})

  // Documents Data
  const [fiscalDocuments, setFiscalDocuments] = useState<FiscalDocumentUI[]>([])
  const [fiscalSequences, setFiscalSequences] = useState<FiscalSequenceUI[]>([])

  // Price Lists Data
  const [priceLevels, setPriceLevels] = useState<PriceLevelUI[]>([])
  const [productPrices, setProductPrices] = useState<ProductPriceUI[]>([])

  // Promotions Data — only marketing_campaigns table exists in schema
  const [marketingCampaigns, setMarketingCampaigns] = useState<MarketingCampaignUI[]>([])

  // Countries Data — tax_jurisdictions + regions tables
  const [taxJurisdictions, setTaxJurisdictions] = useState<TaxJurisdictionUI[]>([])
  const [regions, setRegions] = useState<RegionUI[]>([])

  // UI State
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        fetchData()
      }
    }, DEBOUNCE_DELAY)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch data on tab/subtab change
  useEffect(() => {
    fetchData()
  }, [activeTab, activeSubTab, currentPage])

  /**
   * Main data fetching orchestrator with error handling
   */
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      switch (activeTab) {
        case 'dashboard':
          await fetchDashboardData()
          break
        case 'documents':
          await fetchDocumentsData()
          break
        case 'price-lists':
          await fetchPriceListsData()
          break
        case 'promotions':
          await fetchPromotionsData()
          break
        case 'countries':
          await fetchCountriesData()
          break
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load data'
      setError(errorMessage)
      toast.error(errorMessage)
      console.error('Error fetching data:', err)
    } finally {
      setLoading(false)
    }
  }, [activeTab, activeSubTab, currentPage, searchTerm])

  // =====================================================
  // DATA FETCHING FUNCTIONS (100% Schema Aligned)
  // =====================================================

  const fetchDashboardData = async () => {
    try {
      const res = await apiClient.get('/api/dashboard/analytics')
      const data = res.data?.data || {}
      setAnalytics({
        totalBusinessDays: data.totalBusinessDays || 0,
        openBusinessDays: data.openBusinessDays || 0,
        totalSales: data.totalSales || 0,
        totalTransactions: data.totalTransactions || 0,
        totalTax: data.totalTax || 0,
        activeUsers: data.activeUsers || 0,
        activeStores: data.activeStores || 0,
        paymentTypeCount: data.paymentTypeCount || 0,
        taxRateCount: data.taxRateCount || 0,
        totalProducts: data.totalProducts || 0,
        totalCustomers: data.totalCustomers || 0,
        pendingOrders: data.pendingOrders || 0,
        dailyRevenue: data.dailyRevenue || 0,
        averageOrderValue: data.averageOrderValue || 0
      })
    } catch (err) {
      console.error('Dashboard fetch error:', err)
    }
  }

  const fetchDocumentsData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm
    })

    if (activeSubTab === 'fiscal-documents') {
      try {
        const response = await apiClient.get(`/api/fiscal-documents?${params}`)
        const data = response.data?.data || response.data || []
        setFiscalDocuments(data.map((doc: any) => ({
          id: doc.id,
          document_number: doc.document_number,
          document_type: doc.document_type,
          document_date: doc.issue_date,
          order_id: doc.order_id,
          cash_register_id: doc.cash_register_id,
          total_amount: parseFloat(doc.total_amount || 0),
          tax_amount: parseFloat(doc.tax_amount || 0),
          status: doc.is_voided ? 'voided' : 'issued',
          is_voided: doc.is_voided || false,
          created_at: doc.created_at,
          fiscal_sequence_id: doc.fiscal_sequence_id
        })))
        setTotalCount(response.data?.total || data.length)
      } catch (err) {
        // Fallback to orders if fiscal documents endpoint doesn't exist
        const response = await apiClient.get(`/api/orders?${params}`)
        const orders = response.data?.data || response.data || []
        setFiscalDocuments(orders.map((o: any) => ({
          id: o.id,
          document_number: o.order_number,
          document_type: 'RECEIPT',
          document_date: o.order_date,
          order_id: o.id,
          cash_register_id: o.cash_register_id,
          total_amount: parseFloat(o.total_amount || 0),
          tax_amount: parseFloat(o.tax_amount || 0),
          status: o.status,
          is_voided: o.is_voided || false,
          created_at: o.created_at
        })))
        setTotalCount(response.data?.total || orders.length)
      }
    } else if (activeSubTab === 'sequences') {
      try {
        const response = await apiClient.get('/api/fiscal-sequences')
        const data = response.data || []
        setFiscalSequences(data)
        setTotalCount(data.length)
      } catch (err) {
        setFiscalSequences([])
        setTotalCount(0)
      }
    }
  }

  const fetchPriceListsData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm
    })

    if (activeSubTab === 'levels') {
      try {
        const response = await apiClient.get('/api/price-levels')
        const data = response.data || []
        setPriceLevels(data)
        setTotalCount(data.length)
      } catch (err) {
        console.error('Price levels fetch error:', err)
        setPriceLevels([])
        setTotalCount(0)
      }
    } else if (activeSubTab === 'product-prices') {
      try {
        const response = await apiClient.get(`/api/product-prices?${params}`)
        const data = response.data?.data || response.data || []

        // Fetch product details
        const productsResponse = await apiClient.get('/api/inventory/products')
        const products = productsResponse.data?.data || productsResponse.data || []

        const prices: ProductPriceUI[] = data.map((pp: any) => {
          const product = products.find((p: any) => p.id === pp.product_id)
          return {
            id: pp.id,
            product_id: pp.product_id,
            price_level: pp.price_level,
            price: parseFloat(pp.price || 0),
            effective_date: pp.effective_date,
            product: product ? {
              id: product.id,
              code: product.code,
              name: product.name
            } : undefined
          }
        })

        setProductPrices(prices)
        setTotalCount(response.data?.total || prices.length)
      } catch (err) {
        console.error('Product prices fetch error:', err)
        setProductPrices([])
        setTotalCount(0)
      }
    }
  }

  const fetchPromotionsData = async () => {
    // Only 'campaigns' subtab — marketing_campaigns is the only table that exists in schema
    try {
      const response = await apiClient.get('/api/marketing-campaigns')
      const data = response.data || []
      setMarketingCampaigns(data)
      setTotalCount(data.length)
    } catch (err) {
      console.error('Marketing campaigns fetch error:', err)
      setMarketingCampaigns([])
      setTotalCount(0)
    }
  }

  const fetchCountriesData = async () => {
    if (activeSubTab === 'jurisdictions') {
      try {
        const response = await apiClient.get('/api/tax-jurisdictions')
        setTaxJurisdictions(response.data || [])
        setTotalCount(response.data?.length || 0)
      } catch (err) {
        console.error('Tax jurisdictions fetch error:', err)
        setTaxJurisdictions([])
        setTotalCount(0)
      }
    } else if (activeSubTab === 'regions') {
      try {
        const response = await apiClient.get('/api/regions')
        setRegions(response.data || [])
        setTotalCount(response.data?.length || 0)
      } catch (err) {
        console.error('Regions fetch error:', err)
        setRegions([])
        setTotalCount(0)
      }
    }
  }

  const handleMaximize = () => {
    setIsFullscreen(!isFullscreen)
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  const openConfirmDialog = (props: any) => {
    setConfirmDialogProps(props)
    setShowConfirmDialog(true)
  }

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false)
    setConfirmDialogProps({})
  }

  const confirmAction = () => {
    if (confirmDialogProps.onConfirm) {
      confirmDialogProps.onConfirm()
    }
    closeConfirmDialog()
  }

  // =====================================================
  // COLUMN DEFINITIONS (100% Schema Aligned)
  // =====================================================

  const fiscalDocumentColumns: Column<FiscalDocumentUI>[] = useMemo(() => [
    { 
      key: 'document_number', 
      header: 'Document #', 
      accessor: (row) => row.document_number, 
      sortable: true, 
      filterable: true,
      className: 'font-mono font-semibold'
    },
    { 
      key: 'document_type', 
      header: 'Type', 
      accessor: (row) => row.document_type,
      render: (value) => (
        <span className="px-2 py-1 text-xs rounded-full bg-blue-900 text-blue-200">
          {value}
        </span>
      )
    },
    { 
      key: 'document_date', 
      header: 'Date', 
      accessor: (row) => row.document_date,
      render: (value) => new Date(value).toLocaleDateString(),
      sortable: true
    },
    { 
      key: 'total_amount', 
      header: 'Total', 
      accessor: (row) => row.total_amount,
      align: 'right',
      render: (value) => `$${value.toFixed(2)}`,
      sortable: true,
      className: 'text-white font-bold'
    },
    { 
      key: 'tax_amount', 
      header: 'Tax', 
      accessor: (row) => row.tax_amount,
      align: 'right',
      render: (value) => `$${value.toFixed(2)}`
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (row) => row.is_voided ? 'voided' : row.status,
      align: 'center',
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value === 'completed' || value === 'issued' ? 'bg-green-900 text-green-200' :
          value === 'voided' ? 'bg-red-900 text-red-200' :
          'bg-gray-700 text-gray-400'
        }`}>
          {value.charAt(0).toUpperCase() + value.slice(1)}
        </span>
      )
    }
  ], [])

  const priceLevelColumns: Column<PriceLevelUI>[] = useMemo(() => [
    { key: 'name', header: 'Name', accessor: (row) => row.name, sortable: true, filterable: true },
    { key: 'code', header: 'Code', accessor: (row) => row.code, sortable: true, className: 'font-mono' },
    { 
      key: 'markup_percentage', 
      header: 'Markup %', 
      accessor: (row) => row.markup_percentage,
      align: 'right',
      render: (value) => value ? `${value}%` : '-'
    },
    {
      key: 'is_default',
      header: 'Default',
      accessor: (row) => row.is_default,
      align: 'center',
      render: (value) => value ? <CheckCircle size={16} className="text-blue-400 mx-auto" /> : '-'
    },
    {
      key: 'is_active',
      header: 'Status',
      accessor: (row) => row.is_active,
      align: 'center',
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${value ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ], [])

  const productPriceColumns: Column<ProductPriceUI>[] = useMemo(() => [
    { 
      key: 'product_code', 
      header: 'Product Code', 
      accessor: (row) => row.product?.code || '-',
      sortable: true,
      filterable: true,
      className: 'font-mono'
    },
    { 
      key: 'product_name', 
      header: 'Product Name', 
      accessor: (row) => row.product?.name || '-',
      sortable: true,
      filterable: true 
    },
    { 
      key: 'price_level', 
      header: 'Price Level', 
      accessor: (row) => row.price_level,
      sortable: true
    },
    { 
      key: 'price', 
      header: 'Price', 
      accessor: (row) => row.price,
      align: 'right',
      render: (value) => `$${parseFloat(value.toString()).toFixed(2)}`,
      sortable: true,
      className: 'text-white font-semibold'
    },
    { 
      key: 'effective_date', 
      header: 'Effective Date', 
      accessor: (row) => row.effective_date,
      render: (value) => new Date(value).toLocaleDateString()
    }
  ], [])


  // =====================================================
  // RENDER FUNCTIONS
  // =====================================================

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard()
      case 'documents':
        return renderDocuments()
      case 'price-lists':
        return renderPriceLists()
      case 'promotions':
        return renderPromotions()
      case 'countries':
        return renderCountries()
      default:
        return null
    }
  }

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Main Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Total Sales</span>
            <DollarSign className="text-green-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">${(analytics.totalSales || 0).toFixed(2)}</div>
          <div className="text-gray-500 text-sm mt-1">{analytics.totalTransactions || 0} transactions</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Daily Revenue</span>
            <BarChart3 className="text-blue-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">${(analytics.dailyRevenue || 0).toFixed(2)}</div>
          <div className="text-gray-500 text-sm mt-1">Last 24 hours</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Pending Orders</span>
            <ShoppingCart className="text-yellow-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">{analytics.pendingOrders || 0}</div>
          <div className="text-gray-500 text-sm mt-1">Awaiting processing</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Average Order Value</span>
            <TrendingUp className="text-purple-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">${(analytics.averageOrderValue || 0).toFixed(2)}</div>
          <div className="text-gray-500 text-sm mt-1">Per transaction</div>
        </div>
      </div>

      {/* Secondary Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Active Users</span>
            <UserCheck className="text-green-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">{analytics.activeUsers || 0}</div>
          <div className="text-gray-500 text-sm mt-1">System users</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Active Stores</span>
            <StoreIcon className="text-orange-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">{analytics.activeStores || 0}</div>
          <div className="text-gray-500 text-sm mt-1">Operating locations</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Total Products</span>
            <Package className="text-blue-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">{analytics.totalProducts || 0}</div>
          <div className="text-gray-500 text-sm mt-1">In inventory</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Total Customers</span>
            <Users className="text-purple-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">{analytics.totalCustomers || 0}</div>
          <div className="text-gray-500 text-sm mt-1">Registered customers</div>
        </div>
      </div>

      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">System Overview</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Payment Types</span>
              <span className="text-white font-semibold">{analytics.paymentTypeCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Tax Rates</span>
              <span className="text-white font-semibold">{analytics.taxRateCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Open Business Days</span>
              <span className="text-white font-semibold">{analytics.openBusinessDays || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total Tax Collected</span>
              <span className="text-white font-semibold">${(analytics.totalTax || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button className="p-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors">
              <Plus size={18} className="mx-auto mb-1" />
              New User
            </button>
            <button className="p-3 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm transition-colors">
              <Plus size={18} className="mx-auto mb-1" />
              New Store
            </button>
            <button className="p-3 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm transition-colors">
              <Plus size={18} className="mx-auto mb-1" />
              New Tax Rate
            </button>
            <button className="p-3 bg-orange-600 hover:bg-orange-700 rounded-lg text-white text-sm transition-colors">
              <Plus size={18} className="mx-auto mb-1" />
              New Payment Type
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  const renderDocuments = () => {
    const subTabs = [
      { id: 'fiscal-documents', label: 'Fiscal Documents', icon: FileCheck },
      { id: 'sequences', label: 'Sequences', icon: Hash }
    ]

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id)
                  setCurrentPage(1)
                }}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>

        {activeSubTab === 'fiscal-documents' && (
          <DataTable
            data={fiscalDocuments}
            columns={fiscalDocumentColumns}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            actions={(row) => (
              <div className="flex items-center justify-center gap-2">
                <button className="p-1 hover:bg-gray-700 rounded" title="View">
                  <Eye size={16} className="text-blue-400" />
                </button>
                <button className="p-1 hover:bg-gray-700 rounded" title="Print">
                  <Printer size={16} className="text-green-400" />
                </button>
              </div>
            )}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <FileCheck size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg">No fiscal documents found</div>
              </div>
            }
          />
        )}

        {activeSubTab === 'sequences' && fiscalSequences.length > 0 && (
          <DataTable
            data={fiscalSequences}
            columns={[
              { key: 'sequence_type', header: 'Type', accessor: (row) => row.sequence_type },
              { key: 'prefix', header: 'Prefix', accessor: (row) => row.prefix || '-' },
              { key: 'current_number', header: 'Current Number', accessor: (row) => row.current_number },
              { key: 'year', header: 'Year', accessor: (row) => row.year || '-' },
              {
                key: 'is_active',
                header: 'Status',
                accessor: (row) => row.is_active,
                align: 'center',
                render: (value) => (
                  <span className={`px-2 py-1 text-xs rounded-full ${value ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'}`}>
                    {value ? 'Active' : 'Inactive'}
                  </span>
                )
              }
            ]}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <Hash size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg">No sequences configured</div>
              </div>
            }
          />
        )}
      </div>
    )
  }

  const renderPriceLists = () => {
    const subTabs = [
      { id: 'levels', label: 'Price Levels', icon: Tag },
      { id: 'product-prices', label: 'Product Prices', icon: DollarSign }
    ]

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id)
                  setCurrentPage(1)
                }}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>

        <div className="flex justify-between items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2">
              <Download size={18} />
              Export
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
              <Plus size={18} />
              New
            </button>
          </div>
        </div>

        {activeSubTab === 'levels' && (
          <DataTable
            data={priceLevels}
            columns={priceLevelColumns}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            actions={(row) => (
              <div className="flex items-center justify-center gap-2">
                <button className="p-1 hover:bg-gray-700 rounded" title="Edit">
                  <Edit size={16} className="text-yellow-400" />
                </button>
                <button className="p-1 hover:bg-gray-700 rounded" title="Delete">
                  <Trash2 size={16} className="text-red-400" />
                </button>
              </div>
            )}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <Tag size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg">No price levels found</div>
              </div>
            }
          />
        )}

        {activeSubTab === 'product-prices' && (
          <DataTable
            data={productPrices}
            columns={productPriceColumns}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <DollarSign size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg">No product prices found</div>
              </div>
            }
          />
        )}
      </div>
    )
  }

  const renderPromotions = () => {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-white">Marketing Campaigns</h3>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
            <Plus size={18} />
            New Campaign
          </button>
        </div>

        {marketingCampaigns.length > 0 && (
          <DataTable
            data={marketingCampaigns}
            columns={[
              { key: 'campaign_name', header: 'Campaign Name', accessor: (row) => row.campaign_name },
              { key: 'campaign_code', header: 'Code', accessor: (row) => row.campaign_code || '-' },
              { key: 'campaign_type', header: 'Type', accessor: (row) => row.campaign_type || '-' },
              { key: 'discount_percentage', header: 'Discount %', accessor: (row) => row.discount_percentage ? `${row.discount_percentage}%` : '-' },
              {
                key: 'is_active',
                header: 'Status',
                accessor: (row) => row.is_active,
                align: 'center',
                render: (value) => (
                  <span className={`px-2 py-1 text-xs rounded-full ${value ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'}`}>
                    {value ? 'Active' : 'Inactive'}
                  </span>
                )
              }
            ]}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <TrendingUp size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg">No marketing campaigns found</div>
              </div>
            }
          />
        )}
      </div>
    )
  }

  const renderCountries = () => {
    const subTabs = [
      { id: 'jurisdictions', label: 'Tax Jurisdictions', icon: MapPin },
      { id: 'regions', label: 'Regions', icon: Store }
    ]

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id)
                  setCurrentPage(1)
                }}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>

        {activeSubTab === 'jurisdictions' && taxJurisdictions.length > 0 && (
          <DataTable
            data={taxJurisdictions}
            columns={[
              { key: 'code', header: 'Code', accessor: (row) => row.code },
              { key: 'name', header: 'Name', accessor: (row) => row.name },
              { key: 'country', header: 'Country', accessor: (row) => row.country || '-' },
              { key: 'state', header: 'State', accessor: (row) => row.state || '-' },
              {
                key: 'is_active',
                header: 'Status',
                accessor: (row) => row.is_active,
                align: 'center',
                render: (value) => (
                  <span className={`px-2 py-1 text-xs rounded-full ${value ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'}`}>
                    {value ? 'Active' : 'Inactive'}
                  </span>
                )
              }
            ]}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <MapPin size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg">No tax jurisdictions found</div>
              </div>
            }
          />
        )}

        {activeSubTab === 'regions' && regions.length > 0 && (
          <DataTable
            data={regions}
            columns={[
              { key: 'code', header: 'Code', accessor: (row) => row.code || '-' },
              { key: 'name', header: 'Name', accessor: (row) => row.name },
              { key: 'country', header: 'Country', accessor: (row) => row.country || '-' },
              {
                key: 'is_active',
                header: 'Status',
                accessor: (row) => row.is_active,
                align: 'center',
                render: (value) => (
                  <span className={`px-2 py-1 text-xs rounded-full ${value ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'}`}>
                    {value ? 'Active' : 'Inactive'}
                  </span>
                )
              }
            ]}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <Store size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg">No regions found</div>
              </div>
            }
          />
        )}
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-900">
        {/* Header */}
        <div className="bg-gray-800 px-6 py-3 flex items-center justify-between border-b border-gray-700">
          <div className="flex items-center gap-4">
            <Link to="/app" className="text-gray-400 hover:text-white transition-colors">
              <ChevronLeft size={20} />
            </Link>
            <div className="flex items-center gap-2">
              <LayoutDashboard size={20} className="text-blue-400" />
              <h2 className="text-base font-semibold text-white">Management System</h2>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={fetchData} className="p-2 hover:bg-gray-700 rounded" title="Refresh">
              <RefreshCw size={18} className="text-gray-400" />
            </button>
            <button onClick={handleMaximize} className="p-2 hover:bg-gray-700 rounded" title="Fullscreen">
              <Maximize size={18} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-gray-800 border-b border-gray-700 px-6 overflow-x-auto">
          <div className="flex gap-1">
            {[
              { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
              { key: 'documents', label: 'Documents', icon: FileText },
              { key: 'price-lists', label: 'Price Lists', icon: DollarSign },
              { key: 'promotions', label: 'Promotions', icon: Tag },
              { key: 'countries', label: 'Countries', icon: Globe },
            ].map(tab => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key as TabType)
                    setActiveSubTab(
                      tab.key === 'dashboard' ? 'overview' :
                      tab.key === 'documents' ? 'fiscal-documents' :
                      tab.key === 'price-lists' ? 'levels' :
                      tab.key === 'promotions' ? 'campaigns' :
                      tab.key === 'countries' ? 'jurisdictions' : 'overview'
                    )
                    setCurrentPage(1)
                  }}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-400 bg-gray-750'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-750'
                  }`}
                >
                  <Icon size={16} />
                  <span className="text-sm">{tab.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6">
          {renderContent()}
        </div>

        {/* Confirm Dialog */}
        {showConfirmDialog && (
          <ConfirmDialog
            isOpen={showConfirmDialog}
            title={confirmDialogProps.title}
            message={confirmDialogProps.message}
            onConfirm={confirmAction}
            onClose={closeConfirmDialog}
          />
        )}
      </div>
    </ErrorBoundary>
  )
}